// src/components/effects/Constellation.jsx
// Fondo "constelación de partículas" (kit: BgConstellation). Sin librerías.
import { useEffect, useRef } from "react";

export default function Constellation({
  density = 0.00012,          // partículas por px² (se limita entre 28 y 120)
  linkDistance = 130,         // distancia máxima para unir puntos
  repelRadius = 140,          // radio de repulsión del cursor
  gold = "247,227,179",       // color acento (RGB)
  neutral = "200,205,220",    // color secundario (RGB)
  className = "",
}) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, P = [], raf = 0, visible = true;
    const mouse = { x: -9999, y: -9999 };

    const size = () => {
      const r = canvas.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = W * dpr; canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const n = Math.max(28, Math.min(120, Math.round(W * H * density)));
      P = Array.from({ length: n }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
        r: 1 + Math.random() * 1.6, g: Math.random() < 0.22,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of P) {
        const dx = p.x - mouse.x, dy = p.y - mouse.y, d = Math.hypot(dx, dy);
        if (d < repelRadius && d > 0) { p.x += (dx / d) * 1.2; p.y += (dy / d) * 1.2; }
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
      }
      for (let i = 0; i < P.length; i++) for (let j = i + 1; j < P.length; j++) {
        const a = P[i], b = P[j], d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < linkDistance) {
          ctx.strokeStyle = `rgba(224,185,112,${(1 - d / linkDistance) * 0.22})`;
          ctx.lineWidth = 0.7; ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        }
      }
      for (const p of P) {
        const col = p.g ? gold : neutral;
        const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3);
        grd.addColorStop(0, `rgba(${col},${p.g ? 0.9 : 0.55})`);
        grd.addColorStop(1, `rgba(${col},0)`);
        ctx.fillStyle = grd; ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2); ctx.fill();
      }
    };

    const loop = () => { if (visible) draw(); raf = requestAnimationFrame(loop); };
    const onMove = (e) => { const r = canvas.getBoundingClientRect(); mouse.x = e.clientX - r.left; mouse.y = e.clientY - r.top; };
    const onLeave = () => { mouse.x = mouse.y = -9999; };
    const onVis = () => { visible = !document.hidden; };

    const ro = new ResizeObserver(size); ro.observe(canvas); size();
    const host = canvas.parentElement;
    if (fine) { host.addEventListener("pointermove", onMove); host.addEventListener("pointerleave", onLeave); }
    document.addEventListener("visibilitychange", onVis);
    if (reduce) draw(); else loop();

    return () => {
      cancelAnimationFrame(raf); ro.disconnect();
      host.removeEventListener("pointermove", onMove); host.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [density, linkDistance, repelRadius, gold, neutral]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={className}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: -1, pointerEvents: "none" }}
    />
  );
}
